
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Category, MappingRule, ImportStatistics } from '@/types';

interface ImportExcelFileProps {
  onCategoriesImported: (categories: Category[]) => void;
  onStatisticsGenerated: (statistics: ImportStatistics) => void;
  onShowStatistics: () => void;
}

export const ImportExcelFile: React.FC<ImportExcelFileProps> = ({
  onCategoriesImported,
  onStatisticsGenerated,
  onShowStatistics
}) => {
  const [isImporting, setIsImporting] = useState(false);

  const downloadTemplate = () => {
    const template = [
      {
        CatLevel0: 'Afdracht',
        CatLevel1: 'Afdracht',
        CatLevel2: 'Baten',
        CatLevel3: 'Opbrengsten eigen fondsenwerving',
        CatLevel4: 'Bedrijven',
        CatLevel5: 'Grootzakelijk',
        CatLevel6: 'Grootzakelijk',
        AccountFrom: '86089',
        AccountTo: '86089',
        TeamFrom: '',
        TeamTo: '',
        ProjectFrom: '',
        ProjectTo: '',
        Afdracht: true
      },
      {
        CatLevel0: 'Geen afdracht',
        CatLevel1: 'Geen afdracht',
        CatLevel2: 'Baten',
        CatLevel3: 'Opbrengsten eigen fondsenwerving',
        CatLevel4: 'Major Donors & Foundations',
        CatLevel5: 'Major Donors & Foundations',
        CatLevel6: 'Major Donors & Foundations',
        AccountFrom: '86300',
        AccountTo: '86300',
        TeamFrom: '',
        TeamTo: '',
        ProjectFrom: '',
        ProjectTo: '',
        Afdracht: false
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories Template');
    XLSX.writeFile(wb, 'categories_template.xlsx');
    toast.success('Template downloaded successfully');
  };

  const processExcelData = (data: any[]) => {
    console.log('Processing Excel data:', data);
    if (!data || data.length === 0) {
      toast.error('No data found in the Excel file');
      return;
    }

    const categoryMap: Map<string, Category> = new Map();
    const rootCategories: Category[] = [];
    const mappingRules: { rule: MappingRule, categoryPath: string[] }[] = [];

    data.forEach((row, index) => {
      // Log the Afdracht value from the row
      console.log(`Row ${index + 1} Afdracht value:`, row.Afdracht);

      const categoryLevels = [
        row.CatLevel0,
        row.CatLevel1,
        row.CatLevel2,
        row.CatLevel3,
        row.CatLevel4,
        row.CatLevel5,
        row.CatLevel6
      ].filter(Boolean);

      if (categoryLevels.length === 0) {
        console.warn(`Row ${index + 1} has no category levels`);
        return;
      }

      let currentPath = '';
      let parentCategory: Category | null = null;
      let lastCategory: Category | null = null;

      // Determine the category type based on the Afdracht column
      const isAfdracht = row.Afdracht === true;
      const categoryType: 'Afdracht' | 'Geen afdracht' = isAfdracht ? 'Afdracht' : 'Geen afdracht';

      categoryLevels.forEach((categoryName, level) => {
        if (!categoryName) return;
        
        const pathKey = currentPath ? `${currentPath}/${categoryName}` : categoryName;
        currentPath = pathKey;

        if (!categoryMap.has(pathKey)) {
          const newCategory: Category = {
            id: `cat-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            name: categoryName,
            level,
            sortOrder: level * 1000 + categoryMap.size,
            children: [],
            accounts: [],
            mappingRules: [],
            type: categoryType,
            parentId: parentCategory?.id || null
          };

          categoryMap.set(pathKey, newCategory);

          if (parentCategory) {
            parentCategory.children.push(newCategory);
          } else if (level === 0) {
            rootCategories.push(newCategory);
          }
        }

        lastCategory = categoryMap.get(pathKey) || null;
        parentCategory = lastCategory;
      });

      // Add mapping rule to the last category in the path
      if (row.AccountFrom && row.AccountTo && lastCategory) {
        const mappingRule: MappingRule = {
          id: `rule-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          mainAccountFrom: row.AccountFrom.toString(),
          mainAccountTo: row.AccountTo.toString(),
          teamFrom: row.TeamFrom ? row.TeamFrom.toString() : '',
          teamTo: row.TeamTo ? row.TeamTo.toString() : '',
          projectFrom: row.ProjectFrom ? row.ProjectFrom.toString() : '',
          projectTo: row.ProjectTo ? row.ProjectTo.toString() : ''
        };

        lastCategory.mappingRules.push(mappingRule);
        console.log(`Added mapping rule to category ${lastCategory.name}:`, mappingRule);
      }
    });

    const statistics: ImportStatistics = {
      totalCategories: categoryMap.size,
      rootCategories: rootCategories.length,
      totalMappingRules: Array.from(categoryMap.values()).reduce((total, category) => total + category.mappingRules.length, 0),
      totalAccounts: 0,
      balanceSheetAccounts: 0,
      plAccounts: 0,
      removedAccounts: [],
      changedAccounts: []
    };

    console.log('Final root categories with mapping rules:', rootCategories);
    onStatisticsGenerated(statistics);
    onCategoriesImported(rootCategories);
    onShowStatistics();
    toast.success(`Imported ${categoryMap.size} categories and ${statistics.totalMappingRules} mapping rules`);
  };

  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        
        console.log('Imported Excel data:', jsonData);
        processExcelData(jsonData);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        toast.error('Error processing Excel file');
      } finally {
        setIsImporting(false);
      }
    };

    reader.readAsBinaryString(file);
  }, [onCategoriesImported, onStatisticsGenerated, onShowStatistics]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-4 mb-4">
        <Button 
          variant="outline" 
          onClick={downloadTemplate}
          className="flex items-center gap-2"
          disabled={isImporting}
        >
          <Download className="h-4 w-4" />
          Download Template
        </Button>
      </div>
      
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-[#ff6d00] bg-[#ff6d00]/5' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center gap-2">
          <Upload className={`h-10 w-10 ${isDragActive ? 'text-[#ff6d00]' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-600 font-medium">
            {isImporting 
              ? "Processing..." 
              : isDragActive
                ? "Drop the Excel file here"
                : "Drag 'n' drop Excel file, or click to select"
            }
          </p>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            Upload an Excel file containing categories and mapping rules. The file should have columns for CatLevel0-CatLevel6, AccountFrom, AccountTo, TeamFrom, TeamTo, ProjectFrom, ProjectTo, and Afdracht (true/false).
          </p>
        </div>
      </div>
    </div>
  );
};
